/**
 * Team Admin → Team Users (AC-USER-003, AC-USER-004, AC-USER-007, AC-USER-008).
 *
 * The whole surface is server-driven: `GET /users/v1` does the paging, sorting,
 * persona filtering and email search, so the page works the same on 8 users and
 * on 800. Query state lives in the URL (see `use-team-users.ts`) and survives a
 * refresh.
 *
 * Both mutations are gated behind a confirmation that states the consequence:
 *   persona change → `PATCH /users/v1/{id}` → `rbac.persona_changed` audit row
 *   removal        → `DELETE /users/v1/{id}` → membership soft-deleted, access
 *                    to this team's data ends
 */
import { useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DataTable, type DataTableColumn } from '../../components/data-table';
import { PageHeader } from '../../components/page-header';
import { StatusPill } from '../../components/status-pill';
import { ApiError } from '../../lib/api-error';
import type { CreatedTeamUser, TeamUser } from '../../lib/api-types';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';
import { TableExportButton } from '../integrations/export-dialog';

import { ChangePersonaDialog } from './change-persona-dialog';
import { INVITABLE_PERSONAS, InviteUserDialog } from './invite-user-dialog';
import { buildTeamUsersExportSource } from './team-admin-export';
import { useTeamUsers } from './use-team-users';

type OpenDialog =
  | { kind: 'none' }
  | { kind: 'invite' }
  | { kind: 'persona'; user: TeamUser }
  | { kind: 'remove'; user: TeamUser };

const PERSONA_FILTER_OPTIONS = [
  { value: 'all', label: 'All personas' },
  ...INVITABLE_PERSONAS.map((persona) => ({ value: persona, label: PERSONA_LABELS[persona] })),
];

function memberStatus(user: TeamUser): JSX.Element {
  if (!user.active) return <StatusPill>Removed</StatusPill>;
  if (user.invite_pending) return <StatusPill tone="warning">Invite pending</StatusPill>;
  return <StatusPill tone="success">Active</StatusPill>;
}

export function UserManagementPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { claims } = useSession();
  const controller = useTeamUsers();
  const [dialog, setDialog] = useState<OpenDialog>({ kind: 'none' });
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { query, setQuery, users, total, seats, isLoading, error, reload } = controller;
  const teamName = seats?.team_name ?? 'this team';
  const currentUserId = claims?.sub ?? null;

  async function changePersona(user: TeamUser, persona: Persona): Promise<void> {
    await apiClient.updateTeamUser(user.user_id, { persona });
    setDialog({ kind: 'none' });
    showToast(`Persona updated for ${user.email}.`, 'success');
    reload();
  }

  async function removeUser(user: TeamUser): Promise<void> {
    setRemoveError(null);
    try {
      await apiClient.removeTeamUser(user.user_id);
      setDialog({ kind: 'none' });
      showToast(`${user.email} has been removed from the team.`, 'success');
      reload();
    } catch (caught) {
      setRemoveError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The user could not be removed. Please try again.'
      );
    }
  }

  function afterInvite(created: CreatedTeamUser): void {
    setDialog({ kind: 'none' });
    showToast(
      created.invite_url === null
        ? `User ${created.email} created with the ${PERSONA_LABELS[created.persona ?? 'coach']} persona.`
        : `Invite sent to ${created.email}.`,
      'success'
    );
    reload();
  }

  const columns: DataTableColumn<TeamUser>[] = [
    {
      id: 'email',
      header: 'Email',
      isSortable: true,
      isPinned: true,
      minWidth: 240,
      maxWidth: 420,
      grow: 3,
      render: (user) => (
        <span className="user-cell user-cell--inline" title={user.email}>
          <span className="user-cell__email">{user.email}</span>
        </span>
      ),
      renderDetail: (user) => user.email,
    },
    {
      id: 'name',
      header: 'Name',
      priority: 4,
      minWidth: 140,
      grow: 2,
      render: (user) => (user.display_name ? user.display_name : <span className="muted">—</span>),
    },
    {
      id: 'persona',
      header: 'Persona',
      isSortable: true,
      priority: 1,
      minWidth: 170,
      grow: 1,
      render: (user) => (user.persona === null ? '—' : PERSONA_LABELS[user.persona]),
    },
    {
      id: 'status',
      header: 'Status',
      priority: 2,
      minWidth: 120,
      grow: 0,
      render: memberStatus,
    },
    {
      id: 'mfa',
      header: 'MFA',
      priority: 3,
      minWidth: 70,
      grow: 0,
      render: (user) =>
        user.mfa_enabled ? (
          <StatusPill tone="success">Yes</StatusPill>
        ) : (
          <StatusPill>No</StatusPill>
        ),
    },
  ];

  const openInvite = (): void => setDialog({ kind: 'invite' });
  const isLocked = (user: TeamUser): boolean => !user.active || user.user_id === currentUserId;

  return (
    <section className="stack team-users">
      <PageHeader
        title="Team Users"
        context={
          <>
            {total} {total === 1 ? 'user' : 'users'} in {teamName}
            {seats?.seats_limit != null
              ? ` · ${seats.seats_used} of ${seats.seats_limit} staff seats used`
              : ''}
          </>
        }
        actions={
          <>
            <TableExportButton
              isDisabled={total === 0}
              source={() => buildTeamUsersExportSource(apiClient, query, seats?.team_name ?? null)}
            />
            <button type="button" className="btn btn--primary" onClick={openInvite}>
              Invite User
            </button>
          </>
        }
      />

      <DataTable<TeamUser>
        caption="Team users"
        surface="team-users"
        layoutName="Team users columns"
        columns={columns}
        rows={users}
        getRowId={(user) => user.user_id}
        getRowClassName={(user) => (user.active ? undefined : 'data-table__row--inactive')}
        sort={{ by: query.sortBy, order: query.sortOrder }}
        onSortChange={(sort) =>
          setQuery({ sortBy: sort.by as typeof query.sortBy, sortOrder: sort.order })
        }
        search={{
          value: query.search,
          onChange: (value) => setQuery({ search: value }),
          label: 'Search by email',
          placeholder: 'Search by email',
        }}
        filters={[
          {
            id: 'persona',
            label: 'Persona',
            value: query.persona,
            options: PERSONA_FILTER_OPTIONS,
            onChange: (value) => setQuery({ persona: value as typeof query.persona }),
          },
          {
            id: 'membership',
            label: 'Membership',
            value: query.isShowingRemoved ? 'all' : 'active',
            options: [
              { value: 'active', label: 'Active only' },
              { value: 'all', label: 'Include removed' },
            ],
            onChange: (value) => setQuery({ isShowingRemoved: value === 'all' }),
          },
        ]}
        pagination={{
          page: query.page,
          pageSize: query.pageSize,
          total,
          onPageChange: (page) => setQuery({ page }),
          onPageSizeChange: (pageSize) => setQuery({ pageSize }),
        }}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No users yet"
        emptyDescription="Invite a user to get started."
        emptyAction={
          <button type="button" className="btn btn--primary" onClick={openInvite}>
            Invite a User
          </button>
        }
        actionsWidth={170}
        primaryRowAction={(user) => (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setDialog({ kind: 'persona', user })}
            disabled={isLocked(user)}
          >
            Change Persona
          </button>
        )}
        rowMenu={(user) => [
          {
            id: 'remove',
            label: 'Remove from team',
            tone: 'danger',
            isDisabled: isLocked(user),
            disabledReason: user.active ? 'You cannot remove yourself.' : 'Already removed.',
            onSelect: () => {
              setRemoveError(null);
              setDialog({ kind: 'remove', user });
            },
          },
        ]}
        getRowMenuLabel={(user) => `More actions for ${user.email}`}
      />

      {dialog.kind === 'invite' ? (
        <InviteUserDialog
          seats={seats}
          onCancel={() => setDialog({ kind: 'none' })}
          onCreate={(input) => apiClient.createTeamUser(input)}
          onDone={afterInvite}
        />
      ) : null}

      {dialog.kind === 'persona' ? (
        <ChangePersonaDialog
          user={dialog.user}
          onCancel={() => setDialog({ kind: 'none' })}
          onConfirm={(persona) => changePersona(dialog.user, persona)}
        />
      ) : null}

      {dialog.kind === 'remove' ? (
        <ConfirmDialog
          title="Remove User"
          tone="danger"
          confirmLabel="Remove"
          error={removeError}
          consequence={`Are you sure you want to remove ${dialog.user.email} from ${teamName}? They will lose all access to this team's data and will not be able to sign in to this team again. Their past entries stay in the audit trail.`}
          onCancel={() => setDialog({ kind: 'none' })}
          onConfirm={() => removeUser(dialog.user)}
        />
      ) : null}
    </section>
  );
}
